import React, { useEffect, useRef, useState } from 'react';
import { 
  ClipboardText, 
  Images, 
  MagnifyingGlass, 
  CurrencyDollar, 
  ChatCircle, 
  Star,
  ShieldCheck,
  Scales,
  Bell,
  HardHat,
  Wrench,
  CalendarCheck
} from '@phosphor-icons/react';

const features = [
  {
    icon: CalendarCheck,
    title: 'AI Crew Scheduling',
    description: 'Describe the job and AI builds a phase-by-phase schedule, then matches your best available crew to each phase — delays auto-cascade and everyone stays notified.',
    bgColor: 'bg-[#D95800]',
    isNew: true
  },
  {
    icon: ClipboardText,
    title: 'Project Management',
    description: 'Post jobs, manage applications, and track project progress all in one place.',
    bgColor: 'bg-[#2B4C6F]'
  },
  {
    icon: Images,
    title: 'Worker Portfolios',
    description: 'Workers showcase their best work with photo galleries. Contractors browse visual proof of skills.',
    bgColor: 'bg-[#2B4C6F]'
  },
  {
    icon: MagnifyingGlass,
    title: 'Smart Matching',
    description: 'Find the perfect workers or jobs with our advanced search, filters, and interactive map view.',
    bgColor: 'bg-[#2B4C6F]'
  },
  {
    icon: CurrencyDollar,
    title: 'Secure Escrow Payments',
    description: 'Built-in escrow system with milestones ensures safe and timely payments for all completed work.',
    bgColor: 'bg-emerald-500'
  },
  {
    icon: ChatCircle,
    title: 'Real-Time Messaging',
    description: 'Chat instantly with contractors and workers. Get notified of new messages even when offline.',
    bgColor: 'bg-[#2B4C6F]'
  },
  {
    icon: Star,
    title: 'Reviews & Ratings',
    description: 'Build your reputation with verified reviews from completed projects. See ratings before you hire.',
    bgColor: 'bg-[#FFC107]',
    iconColor: 'text-neutral-900'
  },
  {
    icon: ShieldCheck,
    title: 'Verified Profiles',
    description: 'Members build a verifiable profile with skills and experience, plus optional ID-verification badges. Contractors can request background checks on hires.',
    bgColor: 'bg-neutral-800'
  },
  {
    icon: Scales,
    title: 'Dispute Resolution',
    description: 'Escrow-backed dispute system protects both parties if issues arise during a project.',
    bgColor: 'bg-[#2B4C6F]'
  },
  {
    icon: Bell,
    title: 'Job Alerts',
    description: 'Workers get instant email alerts when new jobs match their skills and preferred locations.',
    bgColor: 'bg-[#2B4C6F]'
  }
];

// Custom hook for intersection observer
const useInView = (options = {}) => {
  const [isInView, setIsInView] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsInView(true);
        // Once visible, stop observing
        if (ref.current) {
          observer.unobserve(ref.current);
        }
      }
    }, {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px',
      ...options
    });

    const node = ref.current;
    if (node) {
      observer.observe(node);
    }

    return () => {
      if (node) {
        observer.unobserve(node);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional mount-only effect
  }, []);

  return [ref, isInView];
};

// Feature card component with individual animation
const FeatureCard = ({ feature, index }) => {
  const [ref, isInView] = useInView();
  const Icon = feature.icon;
  
  // Calculate stagger delay based on index (max 6 per row on large screens)
  const row = Math.floor(index / 3);
  const col = index % 3;
  const delay = (row * 0.1) + (col * 0.15);

  return (
    <div 
      ref={ref}
      className={`group relative bg-white dark:bg-neutral-800 border-2 border-neutral-900 dark:border-neutral-600 rounded-xl p-6 shadow-[4px_4px_0px_#000] dark:shadow-[4px_4px_0px_#2B4C6F] hover:translate-y-1 hover:shadow-[2px_2px_0px_#000] dark:hover:shadow-[2px_2px_0px_#2B4C6F] transition-all duration-300 ${
        isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}
      style={{ 
        transitionDelay: isInView ? `${delay}s` : '0s',
        transitionProperty: 'opacity, transform, box-shadow'
      }}
      data-testid={`feature-card-${index}`}
    >
      {feature.isNew && (
        <span className="absolute top-3 right-3 bg-[#D95800] text-white text-[9px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full border-2 border-neutral-900">New</span>
      )}
      {/* Icon with hover wiggle */}
      <div className={`w-14 h-14 ${feature.bgColor} border-2 border-neutral-900 rounded-xl flex items-center justify-center mb-5 shadow-[3px_3px_0px_#000] group-hover:scale-110 transition-transform duration-300`}>
        <Icon className={`h-7 w-7 ${feature.iconColor || 'text-white'} group-hover:rotate-6 transition-transform duration-300`} weight="bold" />
      </div>
      
      {/* Content with subtle lift on hover */}
      <h3 className="text-xl font-bold text-neutral-900 dark:text-white mb-2 group-hover:text-[#2B4C6F] transition-colors duration-300">
        {feature.title}
      </h3>
      <p className="text-neutral-600 dark:text-neutral-300 leading-relaxed font-medium">
        {feature.description}
      </p>
      
      {/* Animated underline on hover */}
      <div className="mt-4 h-1 w-0 bg-[#2B4C6F] rounded-full group-hover:w-full transition-all duration-500 ease-out" />
    </div>
  );
};

const Features = () => {
  const [headerRef, headerInView] = useInView();
  const [ctaRef, ctaInView] = useInView();

  return (
    <section className="py-16 md:py-24 bg-[#F0F0F2] dark:bg-neutral-900 relative overflow-hidden transition-colors duration-300">
      {/* Construction Pattern Background */}
      <div className="absolute inset-0 opacity-5" 
        style={{
          backgroundImage: `repeating-linear-gradient(
            -45deg,
            transparent,
            transparent 40px,
            rgba(91, 143, 185, 0.3) 40px,
            rgba(91, 143, 185, 0.3) 80px
          )`
        }}
      />
      
      {/* Decorative Elements with float animation */}
      <div className="absolute top-20 left-10 text-[#2B4C6F]/10 animate-float">
        <HardHat className="w-32 h-32" weight="bold" />
      </div>
      <div className="absolute bottom-20 right-10 text-[#2B4C6F]/10 animate-float" style={{ animationDelay: '1.5s' }}>
        <Wrench className="w-28 h-28" weight="bold" />
      </div>
      
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header with scroll animation */}
        <div 
          ref={headerRef}
          className={`text-center mb-12 md:mb-16 transition-all duration-700 ${
            headerInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div 
            className={`inline-flex items-center gap-2 bg-[#2B4C6F] border-2 border-neutral-900 rounded-full px-6 py-2 mb-6 shadow-[4px_4px_0px_#000] transition-all duration-500 ${
              headerInView ? 'scale-100' : 'scale-90'
            }`}
            style={{ transitionDelay: '0.2s' }}
          >
            <span className="text-sm font-bold text-white tracking-wider uppercase">Powerful Features</span>
          </div>
          <h2 
            className={`text-3xl sm:text-4xl lg:text-5xl font-black text-neutral-900 dark:text-white mb-4 tracking-tight transition-all duration-700 ${
              headerInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
            style={{ transitionDelay: '0.3s' }}
          >
            Everything You Need for{' '}
            <span className="text-[#2B4C6F]">Construction Projects</span>
          </h2>
          <p 
            className={`text-lg text-neutral-600 dark:text-neutral-400 max-w-3xl mx-auto font-medium transition-all duration-700 ${
              headerInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
            style={{ transitionDelay: '0.4s' }}
          >
            From finding the right talent to managing payments, ConstructHelp provides all the tools contractors and workers need to succeed together.
          </p>
        </div>

        {/* Features Grid with staggered scroll animations */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((feature, index) => (
            <FeatureCard key={index} feature={feature} index={index} />
          ))}
        </div>

        {/* CTA Banner - Tactical Brutalism Style with scroll animation */}
        <div 
          ref={ctaRef}
          className={`mt-12 md:mt-16 bg-[#2B4C6F] border-2 border-neutral-900 rounded-xl p-8 md:p-10 shadow-[6px_6px_0px_#000] transition-all duration-700 ${
            ctaInView ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-12 scale-95'
          }`}
        >
          <div className="text-center">
            <h3 
              className={`text-2xl md:text-3xl lg:text-4xl font-black text-white mb-3 tracking-tight transition-all duration-500 ${
                ctaInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
              }`}
              style={{ transitionDelay: '0.2s' }}
            >
              Ready to Build Something Great?
            </h3>
            <p 
              className={`text-white/90 mb-8 max-w-2xl mx-auto text-lg font-medium transition-all duration-500 ${
                ctaInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
              }`}
              style={{ transitionDelay: '0.3s' }}
            >
              Connect with skilled professionals and quality projects. Start your construction journey today.
            </p>
            <div 
              className={`flex flex-col sm:flex-row gap-4 justify-center transition-all duration-500 ${
                ctaInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
              }`}
              style={{ transitionDelay: '0.4s' }}
            >
              <a 
                href="/register" 
                className="inline-flex items-center justify-center px-8 py-4 bg-white text-neutral-900 font-bold text-lg rounded-xl border-2 border-neutral-900 shadow-[4px_4px_0px_#000] hover:translate-y-1 hover:shadow-[2px_2px_0px_#000] active:translate-y-2 active:shadow-none transition-all group"
                data-testid="features-get-started-btn"
              >
                Get Started Free
                <span className="ml-2 group-hover:translate-x-1 transition-transform">→</span>
              </a>
              <a 
                href="/pricing" 
                className="inline-flex items-center justify-center px-8 py-4 bg-transparent text-white font-bold text-lg rounded-xl border-2 border-white hover:bg-white/10 transition-all hover:scale-105"
                data-testid="features-view-pricing-btn"
              >
                View Pricing
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Features;
